export type BuyerFaq = {
  question: string
  answer: string
}

export type ProductSeoContent = {
  searchTerms: string[]
  applications: string[]
  buyerConsiderations: string[]
  faq: BuyerFaq[]
}

export type CategorySeoContent = {
  primaryTerm: string
  metaTitle: string
  metaDescription: string
  guide: Array<{ title: string; body: string }>
  faq: BuyerFaq[]
}

const productContent: Record<string, ProductSeoContent> = {
  'hydraulic-knee-joint-a3-hkj': {
    searchTerms: ['A3-HKJ hydraulic knee joint', 'hydraulic prosthetic knee joint supplier', 'single axis hydraulic knee wholesale'],
    applications: [
      'Transfemoral prosthetic fittings that require controlled stance and swing behaviour.',
      'Rehabilitation programmes that progress from supported training to everyday walking.'
    ],
    buyerConsiderations: [
      'Confirm the intended user activity level and clinical fitting plan.',
      'Check alignment, adapter and pylon compatibility before ordering.',
      'Ask for adjustment guidance, quantity pricing and lead time for your destination.'
    ],
    faq: [
      { question: 'What information is needed to quote the A3-HKJ knee joint?', answer: 'Send the model code A3-HKJ, required quantity and destination country. Add any fitting or documentation requirements so the quotation can include lead time and shipping options.' },
      { question: 'Can the A3-HKJ be supplied to distributors and clinics?', answer: 'Yes. Angel Technology supports wholesale and OEM enquiries from prosthetic distributors, clinics and rehabilitation centres.' }
    ]
  },
  'lightweight-knee-joint-a3-r40': {
    searchTerms: ['A3-R40 lightweight knee joint', 'locking prosthetic knee joint supplier', 'aluminium prosthetic knee wholesale'],
    applications: [
      'Transfemoral fittings where low weight and dependable stance stability are priorities.',
      'Basic mobility programmes and everyday prosthetic use for lower functional levels.'
    ],
    buyerConsiderations: [
      'Confirm whether a locking knee design matches the clinical prescription.',
      'Verify the complete connection system and alignment components.',
      'Provide order quantity and destination when requesting wholesale terms.'
    ],
    faq: [
      { question: 'Who is the A3-R40 designed for?', answer: 'It is intended for transfemoral users with lower functional requirements where lightweight construction and stance stability are important.' },
      { question: 'How can a buyer request an A3-R40 quotation?', answer: 'Include the model code, target quantity, destination and any required supporting documents in the website enquiry form.' }
    ]
  },
  'cosmetic-hand-a9-cshhz': {
    searchTerms: ['A9-CSHHZ silicone cosmetic hand', 'silicone prosthetic hand supplier', 'cosmetic hand prosthesis wholesale'],
    applications: [
      'Cosmetic restoration for partial-hand or half-hand amputation.',
      'Daily wear where a natural skin appearance and discreet presentation are priorities.'
    ],
    buyerConsiderations: [
      'Describe the required side, size and skin-tone expectations.',
      'Confirm the amputation level and intended cosmetic coverage.',
      'Ask which measurements or reference images are needed before production.'
    ],
    faq: [
      { question: 'What details are needed for a cosmetic hand enquiry?', answer: 'Provide the model code A9-CSHHZ, required side, approximate size, skin-tone requirement, quantity and destination. The sales team can then confirm what additional fitting information is required.' },
      { question: 'Are different skin tones available?', answer: 'Yes. Multiple skin tones are available; the final selection should be confirmed with the sales team before ordering.' }
    ]
  },
  'cosmetic-sock-a6-vs': {
    searchTerms: ['A6-VS prosthetic cosmetic sock', 'prosthetic stump sock supplier', 'nylon prosthetic sock wholesale'],
    applications: [
      'Daily use between prosthetic components where a soft protective layer is required.',
      'Use alongside compatible silicone liners to help manage friction and wear.'
    ],
    buyerConsiderations: [
      'Confirm the required size and intended liner combination.',
      'State colour and packaging preferences for distributor orders.',
      'Request mixed-quantity options when combining socks with other consumables.'
    ],
    faq: [
      { question: 'Can A6-VS socks be ordered with silicone liners?', answer: 'Yes. Buyers can request a combined quotation and identify the liner models and quantities required.' },
      { question: 'What should distributors include in an A6-VS enquiry?', answer: 'Send the model code, sizes, quantities, destination and any packaging requirements.' }
    ]
  },
  'carbon-fiber-cloth-a5-cfc': {
    searchTerms: ['A5-CFC carbon fiber cloth', 'prosthetic socket carbon fiber cloth supplier', 'carbon fiber lamination material wholesale'],
    applications: [
      'Lamination of custom prosthetic sockets in orthopaedic workshops.',
      'Reinforcement and repair work where high-tensile carbon fabric is specified.'
    ],
    buyerConsiderations: [
      'Confirm the required weave, width and order quantity before quotation.',
      'Describe the lamination process and compatible resin system used by the workshop.',
      'Ask for packing format, lead time and shipping options for the destination.'
    ],
    faq: [
      { question: 'What is the A5-CFC cloth used for?', answer: 'It is a twill carbon-fibre material intended for prosthetic socket lamination, reinforcement and related workshop fabrication.' },
      { question: 'Which details are needed for a wholesale quote?', answer: 'Provide the model code A5-CFC, required quantity, preferred dimensions or packing format and delivery country.' }
    ]
  },
  'carbon-fiber-foot-a1-cfld': {
    searchTerms: ['A1-CFLD carbon fiber prosthetic foot', 'dynamic response prosthetic foot supplier', 'carbon prosthetic foot wholesale'],
    applications: [
      'Below-knee and above-knee modular prostheses requiring energy return.',
      'Active daily walking where reduced fatigue and responsive rollover are desired.'
    ],
    buyerConsiderations: [
      'Confirm the user activity level, foot size and clinical prescription.',
      'Check pyramid, adapter and cosmetic shell compatibility.',
      'Request the appropriate selection information before placing a quantity order.'
    ],
    faq: [
      { question: 'Can the A1-CFLD be used in below-knee and above-knee systems?', answer: 'It is designed for modular prosthetic systems and may be used in both contexts when selected and aligned by a qualified prosthetic professional.' },
      { question: 'What should be included in an A1-CFLD enquiry?', answer: 'Include the model code, required sizes, quantities, destination and the intended modular connection details.' }
    ]
  },
  'below-elbow-myoelectric-hand-a9-bemh': {
    searchTerms: ['A9-BEMH myoelectric hand', 'below elbow myoelectric prosthetic hand supplier', 'EMG prosthetic hand wholesale'],
    applications: [
      'Below-elbow prosthetic systems using residual-muscle EMG control.',
      'Daily gripping tasks that benefit from powered hand and wrist movement.'
    ],
    buyerConsiderations: [
      'Confirm the clinical assessment and available residual-muscle signals.',
      'Check wrist adapter, cosmetic glove and control-system compatibility.',
      'Ask what training, documentation and after-sales support are available.'
    ],
    faq: [
      { question: 'How is the A9-BEMH controlled?', answer: 'The hand uses two-channel EMG signals from residual muscles to control gripping, with wrist rotation support.' },
      { question: 'What information is required for a distributor quotation?', answer: 'Provide the model code, quantity, destination and the intended wrist-adapter and cosmetic-sleeve configuration.' }
    ]
  },
  '3-prong-pyramid-receiver-adapter-a4-r41': {
    searchTerms: ['A4-R41 pyramid receiver adapter', '3 prong prosthetic adapter supplier', 'rotatable pyramid receiver wholesale'],
    applications: [
      'Modular prosthetic assemblies requiring socket-to-pylon alignment adjustment.',
      'Workshop fittings where sagittal, frontal and rotational fine-tuning is needed.'
    ],
    buyerConsiderations: [
      'Confirm the mating pyramid and component dimensions.',
      'Describe the intended socket and pylon configuration.',
      'Request assembly guidance and quantity pricing for distributor orders.'
    ],
    faq: [
      { question: 'What is the A4-R41 used to connect?', answer: 'It is a three-prong rotatable receiver intended to connect and align compatible sockets, pylons and modular prosthetic components.' },
      { question: 'How should compatibility be checked?', answer: 'Send the model numbers or interface details of the mating components before ordering so the sales team can review the combination.' }
    ]
  },
  'sach-foot-adapter-a2-r8-m10': {
    searchTerms: ['A2-R8=M10 SACH foot adapter', 'prosthetic foot alignment adapter supplier', 'sliding angular adapter wholesale'],
    applications: [
      'Lower-limb prosthetic assemblies requiring sliding and angular correction.',
      'Workshop alignment of compatible feet, pylons, sockets and knee components.'
    ],
    buyerConsiderations: [
      'Confirm the required thread and pyramid interfaces.',
      'Check the available adjustment range against the fitting plan.',
      'Provide mating-component details when requesting a compatibility review.'
    ],
    faq: [
      { question: 'What adjustments does the A2-R8=M10 support?', answer: 'The adapter is designed for sliding and angular fine adjustment in compatible lower-limb prosthetic assemblies.' },
      { question: 'What should a clinic send before ordering?', answer: 'Send the model code, quantity and details of the foot and connecting components so compatibility can be checked.' }
    ]
  },
  'lock-tube-clamp-adapter-a4-r21': {
    searchTerms: ['A4-R21 tube clamp adapter', 'prosthetic tube clamp adapter supplier', 'pyramid connector wholesale'],
    applications: [
      'Quick assembly of compatible modular prosthetic tubes and pyramid components.',
      'Workshop systems that require dual-screw locking and repeatable positioning.'
    ],
    buyerConsiderations: [
      'Confirm tube size and mating pyramid dimensions.',
      'Check the intended load path and assembly instructions with the clinician.',
      'Request quantity pricing together with related adapters or pylons.'
    ],
    faq: [
      { question: 'Why does the A4-R21 use dual locking screws?', answer: 'The dual-screw arrangement is designed to provide stable locking in a compatible modular assembly.' },
      { question: 'Can A4-R21 adapters be included in a mixed order?', answer: 'Yes. Buyers can request mixed quantities with other prosthetic adapters and components.' }
    ]
  },
  'prosthetic-liner-asl': {
    searchTerms: ['ASL silicone prosthetic liner', 'air cushion prosthetic liner supplier', 'silicone liner wholesale'],
    applications: [
      'Residual-limb cushioning and suspension in compatible prosthetic sockets.',
      'Daily fittings where friction control and shock absorption are priorities.'
    ],
    buyerConsiderations: [
      'Confirm liner size, suspension method and socket design.',
      'Describe the intended activity level and comfort requirements.',
      'Request care instructions, packing details and mixed-size wholesale options.'
    ],
    faq: [
      { question: 'What does the ASL liner provide?', answer: 'The air-cushion silicone design is intended to provide cushioning, suspension and friction control between the residual limb and socket.' },
      { question: 'Which details are needed for liner selection?', answer: 'Provide required sizes, quantities, suspension method, destination and any fitting requirements for review.' }
    ]
  },
  'shuttle-lock-a6-a10': {
    searchTerms: ['A6-A10 prosthetic shuttle lock', 'prosthetic liner shuttle lock supplier', 'nylon shuttle lock wholesale'],
    applications: [
      'Below-knee socket systems using compatible silicone or gel locking liners.',
      'Mechanical suspension assemblies where secure liner anchoring is required.'
    ],
    buyerConsiderations: [
      'Confirm compatibility with the liner pin and socket fabrication method.',
      'Check the required release arrangement and installation space.',
      'Request matching components, quantity pricing and assembly information.'
    ],
    faq: [
      { question: 'What is the A6-A10 shuttle lock used for?', answer: 'It anchors a compatible locking liner inside the prosthetic socket to support secure mechanical suspension.' },
      { question: 'How should buyers confirm compatibility?', answer: 'Provide the liner, pin and socket-system details with the model code and quantity before ordering.' }
    ]
  }
}

const categoryContent: Record<string, CategorySeoContent> = {
  'knee-joint': {
    primaryTerm: 'prosthetic knee joint supplier',
    metaTitle: 'Prosthetic Knee Joint Supplier | Hydraulic & Locking Knees',
    metaDescription: 'Compare hydraulic and lightweight locking prosthetic knee joints for distributors, clinics and rehabilitation centres. Wholesale and OEM enquiries welcome.',
    guide: [
      { title: 'Choose by mobility goal', body: 'Begin with the clinical mobility goal: early rehabilitation, stable everyday walking or more dynamic community use. The knee mechanism should support the prescribed balance of stance security and swing control.' },
      { title: 'Confirm the complete modular system', body: 'Review adapters, pylons, socket alignment and foot selection as one system. Send the target configuration with your enquiry so connection requirements can be checked before shipment.' }
    ],
    faq: [
      { question: 'Do you supply prosthetic knee joints to distributors?', answer: 'Yes. Angel Technology accepts wholesale and OEM enquiries from distributors, prosthetic clinics and rehabilitation centres.' },
      { question: 'How do hydraulic and locking knees differ?', answer: 'Hydraulic knees manage movement through fluid resistance, while locking designs prioritise stance stability. Final selection should follow a qualified clinical assessment.' }
    ]
  },
  'silicone-prosthesis': {
    primaryTerm: 'silicone prosthesis supplier',
    metaTitle: 'Silicone Prosthesis Supplier | Cosmetic Hands',
    metaDescription: 'Source silicone cosmetic prostheses for partial-hand applications with natural texture and multiple skin-tone options. Wholesale enquiries supported.',
    guide: [
      { title: 'Define the cosmetic coverage', body: 'State the amputation level, required side and coverage area. Clear measurements and reference information help the supplier identify the appropriate cosmetic solution.' },
      { title: 'Review appearance requirements', body: 'Discuss skin tone, texture, nail appearance and intended daily use before confirming the order. These details are central to a discreet cosmetic result.' }
    ],
    faq: [
      { question: 'Can silicone prostheses be supplied in different skin tones?', answer: 'Yes. Multiple skin-tone options are available and should be confirmed during the quotation process.' },
      { question: 'What information should a buyer provide?', answer: 'Provide the product code, side, size or measurements, skin-tone requirement, quantity and destination.' }
    ]
  },
  socks: {
    primaryTerm: 'prosthetic socks supplier',
    metaTitle: 'Prosthetic Socks Supplier | Stump & Cosmetic Socks',
    metaDescription: 'Wholesale prosthetic socks for liner protection, friction management and daily fitting support. Mixed product enquiries available for distributors and clinics.',
    guide: [
      { title: 'Match the sock to the fitting purpose', body: 'Identify whether the priority is friction reduction, liner protection or day-to-day volume management. Confirm the sock and liner combination with the prosthetic professional.' },
      { title: 'Plan repeat supply', body: 'Socks are regular-use consumables. Distributors should specify size mix, packaging and replenishment quantities when requesting a quotation.' }
    ],
    faq: [
      { question: 'Can prosthetic socks be ordered with liners?', answer: 'Yes. Mixed enquiries can include socks, liners and related suspension products.' },
      { question: 'Are wholesale quantities available?', answer: 'Yes. Send the required size mix, quantities and destination for a wholesale quotation.' }
    ]
  },
  material: {
    primaryTerm: 'prosthetic materials supplier',
    metaTitle: 'Prosthetic Materials Supplier | Carbon Fiber Cloth',
    metaDescription: 'Source carbon-fibre cloth and workshop materials for custom prosthetic socket lamination, reinforcement and repair applications.',
    guide: [
      { title: 'Start with the fabrication process', body: 'Specify the socket lamination or repair process, resin system and required reinforcement. Material selection should follow the workshop method and clinical design.' },
      { title: 'Confirm dimensions and packing', body: 'Provide the required weave, width, length and packing preference so a comparable quotation can be prepared.' }
    ],
    faq: [
      { question: 'Do you supply materials to prosthetic workshops?', answer: 'Yes. Angel Technology supports material enquiries from workshops, clinics, distributors and rehabilitation suppliers.' },
      { question: 'What details are needed for carbon-fibre cloth orders?', answer: 'Send the required weave, dimensions, quantity, packing preference and destination.' }
    ]
  },
  foot: {
    primaryTerm: 'carbon fiber prosthetic foot supplier',
    metaTitle: 'Carbon Fiber Prosthetic Foot Supplier | Dynamic Feet',
    metaDescription: 'Source dynamic carbon-fibre prosthetic feet for modular below-knee and above-knee systems. Wholesale and OEM enquiries supported.',
    guide: [
      { title: 'Select for activity and gait goals', body: 'Consider activity level, expected terrain and the desired balance between stability and energy return. A prosthetic professional should make the final clinical selection.' },
      { title: 'Check size and modular compatibility', body: 'Confirm foot size, connection interface, adapter combination and cosmetic shell requirements before placing an order.' }
    ],
    faq: [
      { question: 'Are carbon-fibre feet available for distributor orders?', answer: 'Yes. Wholesale and OEM enquiries are accepted from distributors, clinics and rehabilitation suppliers.' },
      { question: 'What information is needed for foot selection?', answer: 'Provide the model, size requirements, intended modular system, quantity and destination.' }
    ]
  },
  'myoelectric-hand': {
    primaryTerm: 'myoelectric prosthetic hand supplier',
    metaTitle: 'Myoelectric Prosthetic Hand Supplier | Below-Elbow Systems',
    metaDescription: 'Source EMG-controlled below-elbow myoelectric hands for distributors and prosthetic clinics, with compatible wrist and cosmetic components.',
    guide: [
      { title: 'Begin with clinical signal assessment', body: 'Myoelectric control depends on usable residual-muscle signals and a suitable socket plan. Clinical assessment and user training are essential parts of the fitting process.' },
      { title: 'Review the complete upper-limb system', body: 'Confirm the controller, wrist adapter, power system and cosmetic covering as a compatible set before ordering.' }
    ],
    faq: [
      { question: 'What does a myoelectric hand use for control?', answer: 'It uses EMG signals detected from residual muscles to operate powered hand functions.' },
      { question: 'Can clinics request supporting information before ordering?', answer: 'Yes. Include the intended configuration and documentation requirements in the enquiry.' }
    ]
  },
  adapter: {
    primaryTerm: 'prosthetic adapter supplier',
    metaTitle: 'Prosthetic Adapter Supplier | Pyramid & Tube Connectors',
    metaDescription: 'Compare pyramid receivers, foot-alignment adapters and tube clamps for modular prosthetic assembly. Wholesale mixed orders available.',
    guide: [
      { title: 'Map every connection point', body: 'List the socket, knee, pylon and foot components in the intended assembly. Interface dimensions and adjustment needs should be checked across the complete system.' },
      { title: 'Confirm alignment requirements', body: 'Identify whether the fitting needs angular, sliding or rotational adjustment. Use the product code and mating-component details when requesting support.' }
    ],
    faq: [
      { question: 'Can different prosthetic adapters be combined in one order?', answer: 'Yes. Distributors and clinics can request mixed adapter quantities in one quotation.' },
      { question: 'How is adapter compatibility checked?', answer: 'Provide model numbers, interface dimensions and the intended modular configuration before ordering.' }
    ]
  },
  liner: {
    primaryTerm: 'silicone prosthetic liner supplier',
    metaTitle: 'Silicone Prosthetic Liner Supplier | Cushion & Suspension',
    metaDescription: 'Wholesale silicone prosthetic liners for cushioning, suspension and friction control in compatible lower-limb socket systems.',
    guide: [
      { title: 'Choose the suspension method first', body: 'Identify whether the socket uses cushioning, locking or another suspension approach. The liner must be compatible with both the residual limb and socket design.' },
      { title: 'Confirm size and care requirements', body: 'Provide the required size range and request care information for clinic staff, distributors and end users.' }
    ],
    faq: [
      { question: 'What information is needed for a liner quotation?', answer: 'Provide sizes, quantities, suspension method, destination and any packaging requirements.' },
      { question: 'Can mixed liner sizes be supplied?', answer: 'Mixed-size requirements can be included in the enquiry for review and quotation.' }
    ]
  },
  'shuttle-lock': {
    primaryTerm: 'prosthetic shuttle lock supplier',
    metaTitle: 'Prosthetic Shuttle Lock Supplier | Liner Suspension Locks',
    metaDescription: 'Source mechanical shuttle locks for compatible silicone and gel liner suspension in below-knee prosthetic socket systems.',
    guide: [
      { title: 'Check the liner and pin system', body: 'The lock, liner pin and socket fabrication method must work together. Provide the related component details when requesting a compatibility review.' },
      { title: 'Plan installation and release access', body: 'Confirm the available socket space and release arrangement with the prosthetic professional before final assembly.' }
    ],
    faq: [
      { question: 'What does a shuttle lock do?', answer: 'It mechanically anchors a compatible locking liner inside the prosthetic socket to support secure suspension.' },
      { question: 'Can locks be ordered with liners?', answer: 'Yes. Buyers can request a combined quotation for compatible liners, locks and related components.' }
    ]
  }
}

export function getProductSeoContent(slug: string): ProductSeoContent | undefined {
  return productContent[slug]
}

export function getCategorySeoContent(slug: string): CategorySeoContent | undefined {
  return categoryContent[slug]
}
